import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Sparkles, Globe, RefreshCw } from 'lucide-react';
import { regenerateArticle } from '../lib/api/announcements.js';
import { listEgressTargets, getEgressTarget } from '../lib/api/egress.js';
import { listVoices } from '../lib/api/articles.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/modal.js';

/**
 * Subset of ArticleDetail fields we use to default Regenerate to the
 * same routing the article was generated with. Optional — when omitted,
 * the dialog renders only the "Generate New" panel (back-compat for the
 * announcement-detail page where no specific article is in scope).
 */
export interface RegenerateArticleContext {
  voice_name?: string | null;
  persona_slug?: string | null;
  /** Free-text from the DB, e.g. "anthropic-opus", "openai-gpt4o", "system". */
  generated_by?: string | null;
  intended_target_name?: string | null;
  intended_destination_name?: string | null;
  /** WP-14 fresh-slug option — the article being regenerated from. */
  article_id?: string | null;
  published_slug?: string | null;
  publish_status?: string | null;
}

interface RegenerateArticleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  announcementId: string;
  onSuccess: () => void;
  onGenerationStart?: (style: string, provider: string) => void;
  /**
   * When provided (article-detail page), the dialog renders two tabs:
   *   • Regenerate — re-run with this article's current voice/persona/
   *     provider/target. One-click.
   *   • Generate New — full chooser, pre-filled with the same values
   *     so an "untouched submit" still matches the original.
   * Without this prop (announcement-detail page) only the Generate New
   * panel is rendered, identical to the previous behaviour.
   */
  currentArticle?: RegenerateArticleContext;
}

const providers = [
  { value: 'anthropic' as const, label: 'Claude Opus 4.5', description: "Anthropic's most capable model" },
  { value: 'openai' as const, label: 'GPT-4o', description: "OpenAI's most advanced model" },
];

/**
 * Map an article's free-text `generated_by` field to one of the providers
 * the regenerate API understands. `generated_by` is either an
 * `<provider>-<model>` slug (e.g. "anthropic-opus") or a system tag for
 * synthesis lanes (e.g. "synthesis_lanes.close_wrap"). Falls back to
 * 'anthropic' when nothing parseable is present so the user is never
 * stuck on an empty provider selection.
 */
function inferProvider(
  generatedBy: string | null | undefined,
): 'anthropic' | 'openai' {
  if (!generatedBy) return 'anthropic';
  const lower = generatedBy.toLowerCase();
  if (lower.startsWith('openai') || lower.includes('gpt')) return 'openai';
  if (lower.startsWith('anthropic') || lower.includes('claude')) return 'anthropic';
  return 'anthropic';
}

type Tab = 'regenerate' | 'generate_new';

export function RegenerateArticleDialog({
  isOpen,
  onClose,
  announcementId,
  onSuccess,
  onGenerationStart,
  currentArticle,
}: RegenerateArticleDialogProps) {
  const hasArticleContext = !!currentArticle;
  const [tab, setTab] = useState<Tab>(hasArticleContext ? 'regenerate' : 'generate_new');

  const [selectedVoice, setSelectedVoice] = useState<string>(currentArticle?.voice_name || '');
  const [selectedProvider, setSelectedProvider] = useState<'anthropic' | 'openai'>(
    inferProvider(currentArticle?.generated_by),
  );
  const [selectedPersona, setSelectedPersona] = useState<string>(currentArticle?.persona_slug || '');
  const [autoPublish, setAutoPublish] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [selectedDestination, setSelectedDestination] = useState<string>(
    currentArticle?.intended_destination_name || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [avoidanceGuidance, setAvoidanceGuidance] = useState<string>('');
  // WP-14: unpublish the current copy everywhere and retire its slug before
  // regenerating, so the replacement mints a fresh URL from its headline.
  const [freshSlug, setFreshSlug] = useState(false);

  const { data: voicesData, isLoading: voicesLoading } = useQuery({
    queryKey: ['editorial-voices'],
    queryFn: listVoices,
    enabled: isOpen,
  });

  const voices = voicesData?.voices || [];

  // Pick a default voice — prefer the article's current voice, fall back
  // to Orecast Intelligence (the historical default), then any available.
  useEffect(() => {
    if (voices.length === 0 || selectedVoice) return;
    const desired = currentArticle?.voice_name;
    const match = desired
      ? voices.find(v => v.voice_name === desired)
      : voices.find(v => v.voice_name === 'Orecast Intelligence') || voices[0];
    if (match) setSelectedVoice(match.voice_name);
  }, [voices, selectedVoice, currentArticle?.voice_name]);

  // Get personas for the selected voice
  const currentVoice = voices.find(v => v.voice_name === selectedVoice);
  const voicePersonas = currentVoice?.personas || [];

  // Reset persona when voice changes (unless the new voice exposes the
  // article's original persona slug — keep the Regenerate tab's "same as
  // original" intent intact when the user only flips the voice).
  const handleVoiceChange = (voiceName: string) => {
    setSelectedVoice(voiceName);
    const targetVoice = voices.find(v => v.voice_name === voiceName);
    const stillValid = currentArticle?.persona_slug
      ? targetVoice?.personas.some(p => p.slug === currentArticle.persona_slug)
      : false;
    setSelectedPersona(stillValid ? currentArticle!.persona_slug! : '');
  };

  const { data: targetsData } = useQuery({
    queryKey: ['egress-targets'],
    queryFn: listEgressTargets,
    enabled: isOpen,
  });

  const activeTargets = targetsData?.items.filter(t => t.is_active) || [];
  const selectedTarget = activeTargets.find(t => t.target_id === selectedTargetId);

  // When the article carries a target name and the user opts in to
  // auto-publish, pre-select that target so re-publishing lands on the
  // same site by default.
  useEffect(() => {
    if (
      autoPublish
      && currentArticle?.intended_target_name
      && !selectedTargetId
      && activeTargets.length > 0
    ) {
      const t = activeTargets.find(
        x => x.target_name === currentArticle.intended_target_name,
      );
      if (t) setSelectedTargetId(t.target_id);
    }
  }, [autoPublish, currentArticle?.intended_target_name, activeTargets, selectedTargetId]);

  const { data: targetDetail } = useQuery({
    queryKey: ['egress-target', selectedTargetId],
    queryFn: () => getEgressTarget(selectedTargetId),
    enabled: !!selectedTargetId && autoPublish,
  });

  const destinations = targetDetail?.destinations.filter(
    d => d.is_active && d.destination_type !== 'main_branch'
  ) || [];

  /**
   * Submit handler used by both tabs. The Regenerate tab passes the
   * article's pre-filled values verbatim; Generate New uses whatever
   * the user has selected.
   */
  async function submitGeneration(opts: {
    voice: string;
    provider: 'anthropic' | 'openai';
    personaSlug: string;
    targetName?: string;
    destinationName?: string;
    isRegenerateTab: boolean;
  }) {
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (onGenerationStart) {
        onGenerationStart(opts.voice, opts.provider);
      }

      const apiOpts: {
        persona_slug?: string; target_name?: string; destination_name?: string;
        avoidance_guidance?: string; fresh_slug?: boolean; unpublish_article_id?: string;
      } = {};
      if (opts.personaSlug) apiOpts.persona_slug = opts.personaSlug;
      if (opts.targetName) apiOpts.target_name = opts.targetName;
      if (opts.destinationName) apiOpts.destination_name = opts.destinationName;
      const trimmedGuidance = avoidanceGuidance.trim();
      if (trimmedGuidance) apiOpts.avoidance_guidance = trimmedGuidance;
      if (opts.isRegenerateTab && freshSlug && currentArticle?.article_id) {
        apiOpts.fresh_slug = true;
        apiOpts.unpublish_article_id = currentArticle.article_id;
      }

      regenerateArticle(announcementId, opts.voice, opts.provider, apiOpts)
        .then(() => onSuccess())
        .catch((err) => console.error('Article generation failed:', err));

      const msg = opts.targetName
        ? `Article generation started and will auto-publish to ${opts.targetName}.`
        : opts.isRegenerateTab
          ? 'Regeneration started — same voice, persona, and provider as the existing article.'
          : 'Article generation started! Watch the progress card in the articles section below.';
      setSuccessMessage(msg);

      setTimeout(() => {
        onClose();
        setSuccessMessage(null);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start article generation');
      setIsSubmitting(false);
    }
  }

  const handleRegenerateTabSubmit = () => {
    if (!currentArticle?.voice_name) {
      setError('No existing voice on this article — switch to "Generate New" to choose one.');
      return;
    }
    submitGeneration({
      voice: currentArticle.voice_name,
      provider: inferProvider(currentArticle.generated_by),
      personaSlug: currentArticle.persona_slug || '',
      // Regenerate keeps publish status manual — operator publishes when
      // ready. If the article was already published, the next publish
      // re-uses the same slug (mig 076) so the live URL is stable.
      isRegenerateTab: true,
    });
  };

  const handleGenerateNewSubmit = () => {
    submitGeneration({
      voice: selectedVoice,
      provider: selectedProvider,
      personaSlug: selectedPersona,
      targetName: autoPublish && selectedTarget ? selectedTarget.target_name : undefined,
      destinationName: autoPublish && selectedDestination ? selectedDestination : undefined,
      isRegenerateTab: false,
    });
  };

  if (!isOpen) return null;

  const personaDisplayName = currentArticle?.persona_slug
    ? voicePersonas.find(p => p.slug === currentArticle.persona_slug)?.display_name
    : null;
  const inferredProviderLabel = providers.find(
    p => p.value === inferProvider(currentArticle?.generated_by),
  )?.label;

  return (
    <Modal onClose={onClose} canClose={!isSubmitting} size="2xl">
      <ModalHeader>
        <div className="flex items-center space-x-3">
          <Sparkles className="h-6 w-6 text-primary-600" />
          <h2 className="text-xl sm:text-2xl font-bold text-grey-900">
            {hasArticleContext ? 'Generate Article' : 'Generate New Version'}
          </h2>
        </div>
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="text-grey-400 hover:text-grey-600 transition-colors disabled:opacity-50 touch-target"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>

        {hasArticleContext && (
          <div className="flex-shrink-0 border-b border-grey-200 px-6">
            <nav className="-mb-px flex gap-6" aria-label="Generate mode">
              <button
                onClick={() => setTab('regenerate')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  tab === 'regenerate'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
                }`}
              >
                <RefreshCw className="h-4 w-4" />
                Regenerate
              </button>
              <button
                onClick={() => setTab('generate_new')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  tab === 'generate_new'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
                }`}
              >
                <Sparkles className="h-4 w-4" />
                Generate New
              </button>
            </nav>
          </div>
        )}

        <ModalBody>
          {error && (
            <div className="mb-6 p-4 bg-danger-50 border-2 border-danger-200 text-danger-700 rounded-lg">
              <p className="font-semibold">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-success-50 border-2 border-success-200 text-success-700 rounded-lg">
              <p className="font-semibold">{successMessage}</p>
            </div>
          )}

          {tab === 'regenerate' && hasArticleContext ? (
            <>
              <p className="text-grey-600 mb-6 text-base">
                Regenerate this article using the same voice, persona, AI provider,
                and target it was originally generated with. The new version
                replaces the existing one and re-publishes to the same URL when
                you next publish.
              </p>

              <div className="rounded-lg border-2 border-grey-200 bg-grey-50 divide-y divide-grey-200">
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-grey-500">Writing Voice</span>
                  <span className="text-sm font-semibold text-grey-900">
                    {currentArticle?.voice_name || <em className="text-grey-400">(none)</em>}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-grey-500">Persona</span>
                  <span className="text-sm font-semibold text-grey-900">
                    {personaDisplayName || currentArticle?.persona_slug || <em className="text-grey-400">Auto-assign</em>}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm font-medium text-grey-500">AI Provider</span>
                  <span className="text-sm font-semibold text-grey-900">
                    {inferredProviderLabel || 'Claude Opus 4.5'}
                  </span>
                </div>
                {currentArticle?.intended_target_name && (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm font-medium text-grey-500">Target</span>
                    <span className="text-sm font-semibold text-grey-900 inline-flex items-center gap-1.5">
                      <Globe className="h-3.5 w-3.5 text-grey-400" />
                      {currentArticle.intended_target_name}
                      {currentArticle.intended_destination_name
                        ? ` / ${currentArticle.intended_destination_name}`
                        : ''}
                    </span>
                  </div>
                )}
              </div>

              <p className="mt-4 text-xs text-grey-500">
                Need to change voice, persona, or provider? Switch to the
                Generate New tab.
              </p>

              {/* Optional avoidance guidance — surfaced to the LLM as
                  "avoid these patterns from the previous draft". Useful
                  when regenerating after a Quality Gate review_blocked
                  verdict. */}
              <div className="mt-6">
                <label className="block text-sm font-bold text-grey-900 mb-1.5">
                  Avoidance guidance <span className="text-grey-400 font-normal">(optional)</span>
                </label>
                <p className="text-xs text-grey-500 mb-2">
                  Patterns or claims to AVOID in this regeneration. Surfaced
                  to the LLM verbatim. Useful for fixing Quality Gate
                  flags — e.g. "remove editorial commentary about safety
                  record" or "avoid 'substantially more underway' phrasing".
                </p>
                <textarea
                  value={avoidanceGuidance}
                  onChange={e => setAvoidanceGuidance(e.target.value)}
                  rows={3}
                  placeholder="(none — leave blank to regenerate without guidance)"
                  disabled={isSubmitting}
                  className="w-full p-3 border border-grey-200 rounded-lg text-sm leading-relaxed focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y disabled:opacity-50"
                />
              </div>

              {/* WP-14: fresh-slug replacement — only meaningful when the
                  article has a published URL to retire. */}
              {currentArticle?.article_id && currentArticle?.published_slug && (
                <div className="mt-4 p-3 border border-warning-200 bg-warning-50 rounded-lg">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={freshSlug}
                      onChange={e => setFreshSlug(e.target.checked)}
                      disabled={isSubmitting}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-warning-900">
                      <span className="font-bold">Replace the published URL.</span>{' '}
                      Unpublishes the current copy from every site (live first),
                      retires the slug{' '}
                      <code className="text-xs bg-warning-100 px-1 rounded">
                        {currentArticle.published_slug}
                      </code>
                      , and mints a fresh URL from the regenerated headline at
                      next publish. The old URL will return 404 — use this for
                      retraction-class fixes (bad headline, wrong framing), not
                      routine edits.
                    </span>
                  </label>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-grey-600 mb-6 text-base">
                Choose a writing voice, persona, and AI provider. The generation will run in the background.
              </p>

              {/* Voice Selection */}
              <div className="mb-6">
                <label className="block text-sm font-bold text-grey-900 mb-1.5">
                  Writing Voice
                </label>
                <select
                  value={selectedVoice}
                  onChange={(e) => handleVoiceChange(e.target.value)}
                  disabled={voicesLoading}
                  className="w-full border-2 border-grey-200 rounded-lg px-3 py-2.5 text-sm font-semibold text-grey-900 focus:border-primary-500 focus:outline-none bg-surface"
                >
                  {voicesLoading && <option value="">Loading voices...</option>}
                  {voices.map((voice) => (
                    <option key={voice.voice_name} value={voice.voice_name}>
                      {voice.voice_name}
                    </option>
                  ))}
                </select>
                {currentVoice?.description && (
                  <p className="mt-1.5 text-xs text-grey-500">{currentVoice.description}</p>
                )}
              </div>

              {/* Persona Selection */}
              {voicePersonas.length > 0 && (
                <div className="mb-6">
                  <label className="block text-base font-bold text-grey-900 mb-3">
                    Persona
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Auto-assign option */}
                    <label
                      className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedPersona === ''
                          ? 'border-primary-500 bg-primary-50 shadow-sm'
                          : 'border-grey-200 hover:border-primary-300 hover:bg-grey-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="persona"
                        value=""
                        checked={selectedPersona === ''}
                        onChange={() => setSelectedPersona('')}
                        className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="min-w-0">
                        <div className="font-semibold text-grey-900 text-sm">Auto-assign</div>
                        <div className="text-xs text-grey-500 truncate">Let the system choose based on content</div>
                      </div>
                    </label>

                    {voicePersonas.map((persona) => (
                      <label
                        key={persona.slug}
                        className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedPersona === persona.slug
                            ? 'border-primary-500 bg-primary-50 shadow-sm'
                            : 'border-grey-200 hover:border-primary-300 hover:bg-grey-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="persona"
                          value={persona.slug}
                          checked={selectedPersona === persona.slug}
                          onChange={(e) => setSelectedPersona(e.target.value)}
                          className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="min-w-0">
                          <div className="font-semibold text-grey-900 text-sm flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex-shrink-0">
                              {persona.initials}
                            </span>
                            <span className="truncate">{persona.display_name}</span>
                          </div>
                          <div className="text-xs text-grey-500 truncate">{persona.analytical_lens}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Provider Selection */}
              <div className="mb-6">
                <label className="block text-base font-bold text-grey-900 mb-3">
                  AI Provider
                </label>
                <div className="flex gap-3">
                  {providers.map((provider) => (
                    <label
                      key={provider.value}
                      className={`flex-1 flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedProvider === provider.value
                          ? 'border-primary-500 bg-primary-50 shadow-sm'
                          : 'border-grey-200 hover:border-primary-300 hover:bg-grey-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="provider"
                        value={provider.value}
                        checked={selectedProvider === provider.value}
                        onChange={(e) => setSelectedProvider(e.target.value as 'anthropic' | 'openai')}
                        className="mt-1 mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <div className="font-bold text-grey-900 text-sm">{provider.label}</div>
                        <div className="text-xs text-grey-600">{provider.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Auto-Publish */}
              <div className="mb-6 p-4 border-2 border-grey-200 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoPublish}
                    onChange={(e) => {
                      setAutoPublish(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedTargetId('');
                        setSelectedDestination('');
                      }
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 rounded mr-3"
                  />
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-grey-500" />
                    <span className="font-bold text-grey-900 text-sm">Auto-publish after generation</span>
                  </div>
                </label>

                {autoPublish && (
                  <div className="mt-4 space-y-3 pl-7">
                    <div>
                      <label className="block text-sm font-semibold text-grey-700 mb-1.5">Target</label>
                      <select
                        value={selectedTargetId}
                        onChange={(e) => {
                          setSelectedTargetId(e.target.value);
                          setSelectedDestination('');
                        }}
                        className="w-full border-2 border-grey-200 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                      >
                        <option value="">Select target...</option>
                        {activeTargets.map((t) => (
                          <option key={t.target_id} value={t.target_id}>{t.target_name}</option>
                        ))}
                      </select>
                    </div>

                    {destinations.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold text-grey-700 mb-1.5">Destination</label>
                        <select
                          value={selectedDestination}
                          onChange={(e) => setSelectedDestination(e.target.value)}
                          className="w-full border-2 border-grey-200 rounded-lg px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                        >
                          <option value="">Select destination...</option>
                          {destinations.map((d) => (
                            <option key={d.destination_id} value={d.destination_name}>
                              {d.destination_name} ({d.destination_type})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-2.5 border-2 border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Cancel
          </button>
          {tab === 'regenerate' && hasArticleContext ? (
            <button
              onClick={handleRegenerateTabSubmit}
              disabled={isSubmitting || !currentArticle?.voice_name}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Starting...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Regenerate</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleGenerateNewSubmit}
              disabled={isSubmitting || !selectedVoice || (autoPublish && !selectedTargetId)}
              className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Starting Generation...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  <span>{autoPublish ? 'Generate & Publish' : 'Generate'}</span>
                </>
              )}
            </button>
          )}
        </ModalFooter>
    </Modal>
  );
}
